import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import query from '@/api/APIServicesV2'
import { MovementDetail } from '../interfaces/inventory'

export interface UseBatchMovementsOptions
  extends Omit<
    UseQueryOptions<BatchMovementsResponse, AxiosError>,
    'queryKey' | 'queryFn'
  > {
  batchId: number | null
  // Filtros opcionales según la documentación
  operations?: string[]
  categories?: string[]
  areaIds?: number[]
  dateFrom?: string
  dateTo?: string
  userId?: number
  page?: number
  limit?: number
}

export interface BatchMovementsResponse {
  movements: MovementDetail[]
  total?: number
  page?: number
  limit?: number
}

// Create a unique, descriptive key for this query
const BATCH_MOVEMENTS_QUERY_KEY = 'batch-movements'

/**
 * API function to fetch batch movements
 */
const fetchBatchMovements = async (
  batchId: number,
  filters?: {
    operations?: string[]
    categories?: string[]
    areaIds?: number[]
    dateFrom?: string
    dateTo?: string
    userId?: number
    page?: number
    limit?: number
  }
): Promise<BatchMovementsResponse> => {
  // Construir query parameters si existen filtros
  const params = new URLSearchParams()

  if (filters?.operations?.length) {
    filters.operations.forEach((op) => params.append('operations', op))
  }
  if (filters?.categories?.length) {
    filters.categories.forEach((cat) => params.append('categories', cat))
  }
  if (filters?.areaIds?.length) {
    filters.areaIds.forEach((id) => params.append('areaIds', id.toString()))
  }
  if (filters?.dateFrom) {
    params.append('dateFrom', filters.dateFrom)
  }
  if (filters?.dateTo) {
    params.append('dateTo', filters.dateTo)
  }
  if (filters?.userId) {
    params.append('userId', filters.userId.toString())
  }
  if (filters?.page != null) {
    params.append('page', filters.page.toString())
  }
  if (filters?.limit != null) {
    params.append('limit', filters.limit.toString())
  }

  const queryString = params.toString()
  const url = `/containers/batches/${batchId}/movements${queryString ? `?${queryString}` : ''}`

  const response = await query.get(url)
  return response.data as BatchMovementsResponse
}

/**
 * Custom hook to fetch movements for a specific batch
 *
 * @param options Configuration options including batchId, filters and react-query options
 * @returns TanStack React Query result with batch movements
 */
export const useBatchMovements = ({
  batchId,
  operations,
  categories,
  areaIds,
  dateFrom,
  dateTo,
  userId,
  page,
  limit,
  enabled = true,
  staleTime = 5 * 60 * 1000, // 5 minutes
  ...options
}: UseBatchMovementsOptions) => {
  const filters = {
    operations,
    categories,
    areaIds,
    dateFrom,
    dateTo,
    userId,
    page,
    limit,
  }

  return useQuery({
    queryKey: [BATCH_MOVEMENTS_QUERY_KEY, batchId, filters],
    queryFn: () => {
      if (!batchId) {
        throw new Error('Batch ID is required')
      }
      return fetchBatchMovements(batchId, filters)
    },
    enabled: enabled && batchId !== null,
    staleTime,
    ...options,
  })
}

/**
 * Helper function to get the batch movements query key
 * Useful for cache invalidation or prefetching
 */
export const getBatchMovementsQueryKey = (
  batchId: number,
  filters?: {
    operations?: string[]
    categories?: string[]
    areaIds?: number[]
    dateFrom?: string
    dateTo?: string
    userId?: number
  }
) => [BATCH_MOVEMENTS_QUERY_KEY, batchId, filters]
